<template>
  <AcademyLayout @enroll="enrollOpen = true">
    <div class="ac-wrap kn-page">
      <section class="kn-hero">
        <div>
          <p class="kn-kicker">知识资料</p>
          <h1>培训资料库</h1>
          <p>工业视觉培训配套文档、课件与检查清单，按分类筛选后即可下载</p>
        </div>
        <div class="kn-hero__stat">
          <strong>{{ filtered.length }}</strong>
          <span>份资料</span>
        </div>
      </section>

      <div class="kn-filter">
        <button
          v-for="cat in categories"
          :key="cat"
          type="button"
          class="kn-filter__btn"
          :class="{ 'is-active': activeCat === cat }"
          @click="activeCat = cat"
        >
          {{ cat }}
        </button>
      </div>

      <div class="kn-grid">
        <article v-for="item in filtered" :key="item.id" class="kn-card">
          <div class="kn-card__top">
            <span class="kn-card__icon" :class="`kn-card__icon--${typeTone(item.type)}`">
              {{ item.type.slice(0, 1) }}
            </span>
            <span class="kn-card__type">{{ item.type }}</span>
          </div>
          <h3>{{ item.title }}</h3>
          <p class="kn-card__meta">
            <span>{{ item.category }}</span>
            <span>{{ item.size }}</span>
            <span>{{ item.updatedAt }}</span>
          </p>
          <button type="button" class="kn-dl" @click="onDownload(item)">下载资料</button>
        </article>
      </div>

      <p v-if="!filtered.length" class="kn-empty">当前分类暂无资料</p>
    </div>

    <AcademyEnrollModal v-model="enrollOpen" :course="null" />
  </AcademyLayout>
</template>

<script setup>
import { computed, ref } from 'vue'
import { ElMessage } from 'element-plus'
import AcademyLayout from '../components/AcademyLayout.vue'
import AcademyEnrollModal from '../components/AcademyEnrollModal.vue'
import { knowledgeItems } from '../data/trainingCatalog'

const enrollOpen = ref(false)
const activeCat = ref('全部')

const categories = computed(() => {
  const set = new Set(knowledgeItems.map((i) => i.category))
  return ['全部', ...set]
})

const filtered = computed(() => {
  if (activeCat.value === '全部') return knowledgeItems
  return knowledgeItems.filter((i) => i.category === activeCat.value)
})

function typeTone(type) {
  if (type.includes('课件')) return 'orange'
  if (type.includes('清单')) return 'green'
  return 'blue'
}

function onDownload(item) {
  ElMessage.success(`「${item.title}」已加入下载队列（演示）`)
}
</script>

<style scoped>
.ac-wrap {
  width: min(1080px, calc(100% - 40px));
  margin: 0 auto;
}

.kn-page {
  padding: 28px 0 8px;
}

.kn-hero {
  display: flex;
  align-items: flex-end;
  justify-content: space-between;
  gap: 20px;
  padding: 28px;
  background: #fff;
  border: 1px solid #e8eef5;
  border-radius: 14px;
  box-shadow: 0 8px 24px rgba(30, 55, 90, 0.05);
}

.kn-kicker {
  margin: 0 0 8px;
  font-size: 12px;
  font-weight: 700;
  color: #2d8cf0;
  letter-spacing: 0.08em;
}

.kn-hero h1 {
  margin: 0 0 8px;
  font-size: clamp(22px, 2.4vw, 28px);
  color: #152033;
}

.kn-hero p {
  margin: 0;
  max-width: 560px;
  font-size: 14px;
  line-height: 1.7;
  color: #667085;
}

.kn-hero__stat {
  flex-shrink: 0;
  min-width: 96px;
  padding: 14px 18px;
  border-radius: 12px;
  background: linear-gradient(180deg, #f7faff, #f3f7fc);
  border: 1px solid #e8eef5;
  text-align: center;
}

.kn-hero__stat strong {
  display: block;
  font-size: 28px;
  line-height: 1;
  color: #2d8cf0;
  margin-bottom: 6px;
}

.kn-hero__stat span {
  font-size: 12px;
  color: #98a2b3;
}

.kn-filter {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  margin: 18px 0;
}

.kn-filter__btn {
  padding: 7px 14px;
  border-radius: 999px;
  border: 1px solid #e8eef5;
  background: #fff;
  color: #667085;
  font-size: 13px;
  cursor: pointer;
  transition: all 0.15s ease;
}

.kn-filter__btn:hover {
  border-color: #2d8cf0;
  color: #2d8cf0;
}

.kn-filter__btn.is-active {
  background: #2d8cf0;
  color: #fff;
  border-color: #2d8cf0;
  font-weight: 600;
}

.kn-grid {
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: 14px;
}

.kn-card {
  display: flex;
  flex-direction: column;
  gap: 12px;
  padding: 18px;
  background: #fff;
  border: 1px solid #e8eef5;
  border-radius: 14px;
  transition: border-color 0.2s ease, box-shadow 0.2s ease, transform 0.2s ease;
}

.kn-card:hover {
  border-color: rgba(45, 140, 240, 0.35);
  box-shadow: 0 12px 28px rgba(40, 80, 140, 0.08);
  transform: translateY(-2px);
}

.kn-card__top {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 10px;
}

.kn-card__icon {
  width: 42px;
  height: 42px;
  border-radius: 10px;
  display: grid;
  place-items: center;
  color: #fff;
  font-weight: 700;
  font-size: 15px;
  background: #2d8cf0;
}

.kn-card__icon--orange { background: #ff6a00; }
.kn-card__icon--green { background: #19be6b; }
.kn-card__icon--blue { background: #2d8cf0; }

.kn-card__type {
  font-size: 12px;
  font-weight: 600;
  color: #2d8cf0;
  padding: 3px 8px;
  border-radius: 999px;
  background: rgba(45, 140, 240, 0.1);
}

.kn-card h3 {
  margin: 0;
  font-size: 15px;
  line-height: 1.45;
  color: #152033;
  min-height: 2.9em;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
}

.kn-card__meta {
  display: flex;
  flex-wrap: wrap;
  gap: 8px 12px;
  margin: 0;
  font-size: 12px;
  color: #98a2b3;
}

.kn-dl {
  margin-top: auto;
  align-self: flex-start;
  padding: 8px 16px;
  border: none;
  border-radius: 8px;
  background: #ff6a00;
  color: #fff;
  font-size: 13px;
  font-weight: 600;
  cursor: pointer;
}

.kn-dl:hover {
  background: #e85f00;
}

.kn-empty {
  margin: 40px 0;
  text-align: center;
  color: #98a2b3;
  font-size: 14px;
}

@media (max-width: 960px) {
  .kn-grid {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }
}

@media (max-width: 760px) {
  .ac-wrap {
    width: calc(100% - 24px);
  }

  .kn-hero {
    flex-direction: column;
    align-items: stretch;
    padding: 20px;
  }

  .kn-hero__stat {
    width: fit-content;
  }

  .kn-grid {
    grid-template-columns: 1fr;
  }
}
</style>
